import { useEffect, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { RadioButton } from "primereact/radiobutton";
import Loader from "../../../../components/loader";
import { ModelSelectionFormEntity, SaveModelSelectionReqEntity } from "../../../../interface/tbs-tracker/send-tbs";
import { NUMBER_ONLY_PATTERN } from "../../../../utils/pattern";
import { TBSTypeEnumEntity } from "../../../../utils/enum";
import { APIGetCategoryDDL, APISaveModelSelection } from "../../../../service/api-service";
import { APIResponseEntity } from "../../../../interface/api-responce";
import { toastOptObj } from "../../../../utils/constants";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import { checkValidInputOnKeyDown, removeSpaceOnFirstCharacter } from "../../../../service/shared.service";
import { IS_TYPE_OF_TBS_VISIBLE } from "../../../../utils/tbs-verification";
interface Props {
    tbsId: string;
    modelData: SaveModelSelectionReqEntity;
    onNext: () => void;
}
const ModelSelectionStep = ({ tbsId, modelData, onNext, }: Props) => {
    const navigate = useNavigate()
    const [typeOfTbs, setTypeOfTbs] = useState<TBSTypeEnumEntity>(
        TBSTypeEnumEntity.SKIN
    );
    const [categoryOptions, setCategoryOptions] = useState<
        { _id: string; name: string }[]
    >([]);
    const [isBranded, setIsBranded] = useState<boolean>(true);
    const [category, setCategory] = useState<string | null>(null);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [formData, setFormData] = useState<ModelSelectionFormEntity>({
        brandName: "",
        brandCode: "",
        thickness: "",
        category: "",
        qty: "",
        width: "",
        height: "",
    });
    const handleChange = (key: string, value: string) => {
        setFormData((prev) => ({
            ...prev,
            [key]: value,
        }));
    };
    const showFormValueError = (field: keyof typeof formData) => {
        let message = "";

        switch (field) {
            case "brandName":
                if (isBranded && !formData.brandName.trim()) {
                    message = "Please enter brand name!";
                }
                break;

            case "brandCode":
                if (isBranded && !formData.brandCode.trim()) {
                    message = "Please enter brand code!";
                }
                break;

            case "thickness":
                if (typeOfTbs === TBSTypeEnumEntity.HARDWARE) break;
                if (!formData.thickness.trim()) {
                    message = "Please enter thickness!";
                } else if (
                    parseFloat(formData.thickness) < 1 ||
                    parseFloat(formData.thickness) > 100000
                ) {
                    message = "Thickness should be from 1 to 100000";
                }
                break;

            case "qty":
                if (!formData.qty.trim()) {
                    message = "Please enter quantity!";
                } else if (
                    parseFloat(formData.qty) < 1 ||
                    parseFloat(formData.qty) > 100000
                ) {
                    message = "Quantity should be from 1 to 100000";
                }
                break;

            case "width":
                if (typeOfTbs === TBSTypeEnumEntity.HARDWARE) break;
                if (!formData.width.trim()) {
                    message = "Please enter width!";
                } else if (
                    parseFloat(formData.width) < 1 ||
                    parseFloat(formData.width) > 100000
                ) {
                    message = "Width should be from 1 to 100000";
                }
                break;

            case "height":
                if (typeOfTbs === TBSTypeEnumEntity.HARDWARE) break;
                if (!formData.height.trim()) {
                    message = "Please enter height!";
                } else if (
                    parseFloat(formData.height) < 1 ||
                    parseFloat(formData.height) > 100000
                ) {
                    message = "Height should be from 1 to 100000";
                }
                break;

            case "category":
                if (!category) {
                    message = "Please select category!";
                }
                break;
        }

        if (!isSubmitted || !message) return null;

        return (
            <small
                className="text-danger font-weight-500 p-1"
                data-error="step-error"
                data-field={field}
            >
                {message}
            </small>
        );
    };


    const fetchCategoryDDL = async (tbsType: TBSTypeEnumEntity) => {
        const resp = await APIGetCategoryDDL(tbsType);
        if (!resp || !resp.data || resp.status !== 200) return;
        const apiResp: APIResponseEntity = resp.data;

        if (apiResp.statusCode === 200 && apiResp.data && apiResp.status) {
            setCategoryOptions(apiResp.data);
        } else {
            toast.error(apiResp.message || apiResp.errorMsg, {
                ...toastOptObj,
            });
        }
    };
    // the categories offered always belong to the selected type (also for a draft that loads as hardware)
    useEffect(() => {
        fetchCategoryDDL(typeOfTbs);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [typeOfTbs]);

    useEffect(() => {
        if (!modelData) return;

        setTypeOfTbs(modelData.typeOfTbs || TBSTypeEnumEntity.SKIN);
        setCategory(modelData.categoryId || "");
        setIsBranded(modelData.isBrand ?? true);
        setFormData({
            brandName: modelData.brandName || "",
            brandCode: modelData.brandCode || "",
            thickness:
                (modelData.thickness !== undefined &&
                    modelData.thickness !== null &&
                    modelData.thickness.toString()) ||
                "",
            category: modelData.categoryId || "",
            qty:
                (modelData.qty !== undefined &&
                    modelData.qty !== null &&
                    modelData.qty.toString()) ||
                "",
            width:
                (modelData.width !== undefined &&
                    modelData.width !== null &&
                    modelData.width.toString()) ||
                "",
            height:
                (modelData.height !== undefined &&
                    modelData.height !== null &&
                    modelData.height.toString()) ||
                "",
        });
    }, [modelData]);

    const handleSubmitStep = async () => {
        setIsLoading(true);
        try {
            const errorElement = document.querySelector(
                '[data-error="step-error"]'
            );

            if (errorElement) {
                errorElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
                return;
            }

            const reqBody: SaveModelSelectionReqEntity = {
                typeOfTbs,
                categoryId: category ?? "",
                isBrand: isBranded,
                brandName: isBranded ? formData.brandName.trim() : "",
                brandCode: isBranded ? formData.brandCode.trim() : "",
                thickness: Number(formData.thickness),
                qty: Number(formData.qty),
                width: Number(formData.width),
                height: Number(formData.height),
            };

            const resp = await APISaveModelSelection(tbsId, reqBody);
            if (!resp || (resp && (resp.status !== 200 || !resp.data))) { return; }
            const apiRespPayload: APIResponseEntity = resp.data;
            if (apiRespPayload.statusCode !== 200 || !apiRespPayload.data) {
                toast.error(apiRespPayload.message || apiRespPayload.errorMsg,
                    { ...toastOptObj });
                return;
            }
            onNext();
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };
    return (
        <>
            <Loader isLoading={isLoading} />
            <div className="card mb-4" style={IS_TYPE_OF_TBS_VISIBLE ? undefined : { display: "none" }}>
                <div className="card-body">
                    <div>
                        <div className="form-group">
                            <label> TYPE OF TBS </label>
                            <div className="d-flex align-items-center mt-2">
                                <div className="d-flex align-items-center me-4">
                                    <RadioButton
                                        inputId="skin"
                                        value={TBSTypeEnumEntity.SKIN}
                                        checked={typeOfTbs === TBSTypeEnumEntity.SKIN}
                                        onChange={(e) => {
                                            setTypeOfTbs(e.value);
                                            setCategory(null);
                                        }}
                                    />
                                    <label
                                        htmlFor="skin"
                                        className="mb-0 ms-2"
                                    >
                                        Skin
                                    </label>
                                </div>
                                <div className="d-flex align-items-center">
                                    <RadioButton
                                        inputId="hardware"
                                        value={TBSTypeEnumEntity.HARDWARE}
                                        checked={typeOfTbs === TBSTypeEnumEntity.HARDWARE}
                                        onChange={(e) => {
                                            setTypeOfTbs(e.value);
                                            setCategory(null);
                                        }}
                                    />
                                    <label
                                        htmlFor="hardware"
                                        className="mb-0 ms-2"
                                    >
                                        Hardware
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="card">
                <div className="card-body">
                    <h4 className="card-title mb-4">
                        Model Selection
                    </h4>
                    <div className="row">
                        <div className="col-md-4 mb-2">
                            <div className="form-group">
                                <label> CATEGORY </label>
                                <sup className="mandatory">*</sup>
                                <Dropdown
                                    value={category}
                                    options={categoryOptions}
                                    optionLabel="name"
                                    optionValue="_id"
                                    placeholder="Select Category"
                                    onChange={(e) => setCategory(e.value)}
                                    className={`w-100 ${isSubmitted && !category ? "p-invalid" : ""
                                        }`}
                                />
                                {showFormValueError("category")}
                            </div>
                        </div>
                    </div>
                    <div className="mb-3">
                        <div className="form-group">
                            <label> SELECT BRANDED </label>
                            <div className="d-flex align-items-center mt-2">
                                <div className="d-flex align-items-center me-4">
                                    <RadioButton
                                        inputId="brand"
                                        value={true}
                                        checked={isBranded}
                                        onChange={(e) => setIsBranded(e.value)}
                                    />
                                    <label
                                        htmlFor="brand"
                                        className="mb-0 ms-2"
                                    >
                                        Branded
                                    </label>
                                </div>
                                <div className="d-flex align-items-center">
                                    <RadioButton
                                        inputId="nonbrand"
                                        value={false}
                                        checked={!isBranded}
                                        onChange={(e) => {
                                            setIsBranded(e.value);
                                            setFormData((prev) => ({
                                                ...prev,
                                                brandName: "",
                                                brandCode: "",
                                            }));
                                        }}
                                    />
                                    <label
                                        htmlFor="nonbrand"
                                        className="mb-0 ms-2"
                                    >
                                        Non Branded
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row mt-2">
                        <div className="col-md-4 mb-2">
                            <div className="form-group">
                                <label> BRAND NAME </label>
                                {isBranded && <sup className="mandatory">*</sup>}

                                <InputText
                                    className="form-control form-control-lg"
                                    value={formData.brandName}
                                    disabled={!isBranded}
                                    onChange={(e) =>
                                        handleChange("brandName", e.target.value)
                                    }
                                />
                                {showFormValueError("brandName")}
                            </div>
                        </div>
                        <div className="col-md-4 mb-2">
                            <div className="form-group">
                                <label> BRAND CODE </label>
                                {isBranded && <sup className="mandatory">*</sup>}
                                <InputText
                                    className="form-control form-control-lg"
                                    value={formData.brandCode}
                                    disabled={!isBranded}
                                    onChange={(e) =>
                                        handleChange("brandCode", e.target.value)
                                    }
                                />
                                {showFormValueError("brandCode")}
                            </div>
                        </div>
                        <div className="col-md-4 mb-2">
                            <div className="form-group">
                                <label> QTY </label>
                                <sup className="mandatory">*</sup>
                                <InputText
                                    className="form-control form-control-lg"
                                    value={formData.qty}
                                    onKeyDown={(event: any) => {
                                        checkValidInputOnKeyDown(event, NUMBER_ONLY_PATTERN);
                                        removeSpaceOnFirstCharacter(event);
                                    }}
                                    onChange={(e) =>
                                        handleChange("qty", e.target.value)
                                    }
                                />
                                {showFormValueError("qty")}
                            </div>
                        </div>
                        <div className="col-md-4 mb-2">
                            <div className="form-group">
                                <label>THICKNESS(mm)</label>
                                <sup className="mandatory">*</sup>
                                <InputText
                                    className="form-control form-control-lg"
                                    value={formData.thickness}
                                    disabled={typeOfTbs === TBSTypeEnumEntity.HARDWARE}
                                    onKeyDown={(event: any) => {
                                        checkValidInputOnKeyDown(event, NUMBER_ONLY_PATTERN);
                                        removeSpaceOnFirstCharacter(event);
                                    }}
                                    onChange={(e) =>
                                        handleChange("thickness", e.target.value)
                                    }
                                />
                                {showFormValueError("thickness")}
                            </div>
                        </div>

                        <div className="col-md-4 mb-2">
                            <div className="form-group">
                                <label>WIDTH(mm)</label>
                                <sup className="mandatory">*</sup>
                                <InputText
                                    className="form-control form-control-lg"
                                    value={formData.width}
                                    disabled={typeOfTbs === TBSTypeEnumEntity.HARDWARE}
                                    onKeyDown={(event: any) => {
                                        checkValidInputOnKeyDown(event, NUMBER_ONLY_PATTERN);
                                        removeSpaceOnFirstCharacter(event);
                                    }}
                                    onChange={(e) =>
                                        handleChange("width", e.target.value)
                                    }
                                />
                                {showFormValueError("width")}
                            </div>
                        </div>

                        <div className="col-md-4 mb-2">
                            <div className="form-group">
                                <label>HEIGHT(mm)</label>
                                <sup className="mandatory">*</sup>
                                <InputText
                                    className="form-control form-control-lg"
                                    value={formData.height}
                                    disabled={typeOfTbs === TBSTypeEnumEntity.HARDWARE}
                                    onKeyDown={(event: any) => {
                                        checkValidInputOnKeyDown(event, NUMBER_ONLY_PATTERN);
                                        removeSpaceOnFirstCharacter(event);
                                    }}
                                    onChange={(e) =>
                                        handleChange("height", e.target.value)
                                    }
                                />
                                {showFormValueError("height")}
                            </div>
                        </div>

                    </div>
                </div>
            </div>
            <div className="row mt-3">
                <div className="col-md-12 d-flex align-items-center justify-content-between">
                    <button
                        type="button"
                        className="btn btn-primary btn-md font-weight-medium m-0 me-2"
                        onClick={() => navigate("/TBS/send-for-tbs")}
                    >
                        Previous
                    </button>
                    <button
                        type="submit"
                        className="btn btn-success btn-md font-weight-medium m-0"
                        onClick={() => {
                            setIsSubmitted(true);
                            setTimeout(() => {
                                handleSubmitStep();
                            }, 100);
                        }}
                    >
                        Save & Next
                    </button>
                </div>
            </div>
        </>
    );
};

export default ModelSelectionStep;